import { getStaticShells } from "@/object-actions/indexManifest";
import { resolveObjectActions } from "@/object-actions/resolveObjectActions";
import type {
  ActionScope,
  ActionSurface,
  ObjectActionHandlers,
  ObjectKind,
  ResolvedObjectAction,
  StaticActionShell,
} from "@/object-actions/types";
import { objectKindKey } from "@/object-actions/types";
import {
  buildObjectActionContext,
  type BuildObjectActionContextParams,
} from "@/object-actions/useObjectActionContext";
import { useMemo } from "react";

const EMPTY_ACTIONS: ResolvedObjectAction[] = [];

export interface UseObjectActionsParams extends BuildObjectActionContextParams {
  handlers: ObjectActionHandlers;
  /** When false, skip runtime resolution (e.g. closed rail/context menus). */
  enabled?: boolean;
}

export function useObjectActions({
  kind,
  surface,
  scope,
  permissions,
  state,
  handlers,
  enabled = true,
}: UseObjectActionsParams): {
  actions: ResolvedObjectAction[];
  shells: readonly StaticActionShell[];
  kindKey: string;
} {
  const kindKey = objectKindKey(kind);

  const shells = useMemo(
    () => getStaticShells(kindKey, surface, kind),
    [kind, kindKey, surface],
  );

  const context = useMemo(
    () =>
      buildObjectActionContext({
        kind,
        surface,
        scope,
        permissions,
        state,
      }),
    [kind, surface, scope, permissions, state],
  );

  const actions = useMemo(
    () =>
      enabled
        ? resolveObjectActions(kindKey, surface, context, handlers)
        : EMPTY_ACTIONS,
    [enabled, kindKey, surface, context, handlers],
  );

  return { actions, shells, kindKey };
}

export type { ActionScope, ActionSurface, ObjectKind };
